function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { stableStringify } from "@sail/utils";
import { buildQueryAndVariablesHash } from "../queryUtils/queryAndVariablesHashing";
const DEFAULT_STALE_TIME = 30 * 1000;
class ResultStore {
    getStalenessState(hash) {
        const staleTimestamp = this.queryStalenessMap.get(hash);
        if (typeof staleTimestamp === 'undefined') {
            return 'NOT_FOUND';
        }
        return Date.now() > staleTimestamp ? 'STALE' : 'FRESH';
    }
    addStalenessTimestamp(hash, timestamp = Date.now()) {
        this.queryStalenessMap.set(hash, timestamp + this.staleTime);
    }
    invalidateQueriesByDocumentHash(hashes) {
        for (const [cacheKey] of this.queryStalenessMap){
            for (const hash of hashes){
                if (cacheKey.startsWith(hash)) {
                    this.queryStalenessMap.set(cacheKey, 0);
                    break;
                }
            }
        }
    }
    refreshStaleness(query, variables) {
        const { documentHashWithVariables: key } = buildQueryAndVariablesHash(query, variables);
        this.addStalenessTimestamp(key);
    }
    watchResults(query, variables, callback) {
        const { documentHashWithVariables: key } = buildQueryAndVariablesHash(query, variables);
        const watcherInfo = {
            query,
            variables,
            callback
        };
        if (!this.watchers.has(key)) {
            this.watchers.set(key, new Set());
        }
        this.watchers.get(key).add(watcherInfo);
        let lastDataValue = this.getDataFromCache(query, variables);
        let lastErrorValue = this.getErrorsFromCache(query, variables);
        const notifyCallback = ()=>{
            // Don't notify if we're setting results, as it will cause a double render
            if (!this.isSettingResultsMap.get(key)) {
                callback({
                    data: lastDataValue,
                    errors: lastErrorValue
                });
            }
        };
        const memoryCacheCleanup = this.memoryCache.watch({
            query,
            variables,
            callback: (data)=>{
                lastDataValue = data;
                notifyCallback();
            }
        });
        const errorCacheCleanup = this.errorCache.observe(query, variables, (errors)=>{
            lastErrorValue = errors;
            notifyCallback();
        });
        return ()=>{
            memoryCacheCleanup();
            errorCacheCleanup();
            const watcherSet = this.watchers.get(key);
            if (watcherSet) {
                watcherSet.delete(watcherInfo);
                if (watcherSet.size === 0) {
                    this.watchers.delete(key);
                }
            }
        };
    }
    setFetchingResults(query, variables, result) {
        const { documentHashWithVariables: key } = buildQueryAndVariablesHash(query, variables);
        const currentData = this.getDataFromCache(query, variables);
        const currentErrors = this.getErrorsFromCache(query, variables);
        // Set a lock to avoid double rerenders provoked by both errors and data updates
        this.isSettingResultsMap.set(key, true);
        if (result.data) {
            this.memoryCache.writeQuery({
                query,
                variables,
                data: result.data
            });
        }
        if (result.errors) {
            this.errorCache.setErrors(query, variables, result.errors);
        } else {
            this.addStalenessTimestamp(key);
            this.errorCache.clearErrors(query, variables);
        }
        // Release a lock
        this.isSettingResultsMap.set(key, false);
        const dataAfterUpdate = this.getDataFromCache(query, variables);
        const errorsAfterUpdate = this.getErrorsFromCache(query, variables);
        const hasChanged = stableStringify(currentData) !== stableStringify(dataAfterUpdate) || stableStringify(currentErrors) !== stableStringify(errorsAfterUpdate);
        if (hasChanged) {
            const updatedResult = {
                data: dataAfterUpdate,
                errors: errorsAfterUpdate
            };
            this.triggerWatchCallbacks(query, variables, updatedResult);
        }
    }
    getDataFromCache(query, variables) {
        return this.memoryCache.readQuery({
            query,
            variables
        });
    }
    getErrorsFromCache(query, variables) {
        return this.errorCache.getErrors(query, variables);
    }
    getCurrentResult(query, variables) {
        return {
            data: this.getDataFromCache(query, variables),
            errors: this.getErrorsFromCache(query, variables)
        };
    }
    triggerWatchCallbacks(query, variables, result) {
        const { documentHashWithVariables: key } = buildQueryAndVariablesHash(query, variables);
        const watcherSet = this.watchers.get(key);
        if (watcherSet) {
            watcherSet.forEach((watcher)=>{
                watcher.callback(result);
            });
        }
    }
    constructor(memoryCache, errorCache, staleTime = DEFAULT_STALE_TIME){
        _define_property(this, "memoryCache", void 0);
        _define_property(this, "errorCache", void 0);
        _define_property(this, "queryStalenessMap", void 0);
        _define_property(this, "watchers", new Map());
        _define_property(this, "staleTime", void 0);
        _define_property(this, "isSettingResultsMap", new Map());
        this.memoryCache = memoryCache;
        this.errorCache = errorCache;
        this.queryStalenessMap = new Map();
        this.staleTime = staleTime;
    }
}
export { ResultStore as default };

//# sourceMappingURL=ResultStore.js.map